import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Typography, Box, keyframes } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { WordNode } from '../types';
import { WordTile, DeleteButton, CancelButton } from './StyledComponents';

const pulse = keyframes`
  0% {
    opacity: 1;
    transform: scale(1) translate(-3px, -3px);
    box-shadow: 6px 6px 0px 0px rgba(0,0,0,1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.98) translate(-2px, -2px);
    box-shadow: 4px 4px 0px 0px rgba(0,0,0,1);
  }
  100% {
    opacity: 1;
    transform: scale(1) translate(-3px, -3px);
    box-shadow: 6px 6px 0px 0px rgba(0,0,0,1);
  }
`;

interface SortableWordTileProps {
  node: WordNode;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  isSolved: boolean;
  onDelete: () => void;
  onCancel?: () => void;
}

export const SortableWordTile: React.FC<SortableWordTileProps> = ({
  node,
  index,
  isFirst,
  isLast,
  isSolved,
  onDelete,
  onCancel
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id, disabled: isFirst || isLast || node.pendingValidation });

  const style = {
    opacity: isDragging ? 0.4 : 1,
    backgroundColor: node.pendingValidation ? '#FFFFFF' : (isSolved ? '#98D87D' : '#FFE4A3'),
    '&:hover': {
      transform: !isFirst && !isLast && !node.pendingValidation ? 'translate(0px, 0px)' : 'translate(-3px, -3px)',
      boxShadow: !isFirst && !isLast && !node.pendingValidation ? '3px 3px 0px 0px rgba(0,0,0,1)' : '6px 6px 0px 0px rgba(0,0,0,1)',
    },
    border: '3px solid black',
    cursor: !isFirst && !isLast && !node.pendingValidation ? 'grab' : 'default',
    position: 'relative',
    animation: node.pendingValidation ? `${pulse} 1.5s ease-in-out infinite` : 'none',
    '& .MuiTypography-root:not(.delete-x)': {
      color: node.pendingValidation ? '#CCCCCC' : 'black',
      fontFamily: '"Public Sans", Arial, sans-serif',
      fontWeight: 900,
      fontSize: (() => {
        const length = node.word.length;
        if (length <= 4) return '2rem';
        if (length <= 6) return '1.75rem';
        if (length <= 8) return '1.5rem';
        if (length <= 10) return '1.25rem';
        return '1rem';
      })(),
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }
  };

  return (
    <Box sx={{ transform: CSS.Transform.toString(transform), transition }}>
      <WordTile
        ref={setNodeRef}
        elevation={0}
        sx={style}
        {...attributes}
        {...(!isFirst && !isLast && !node.pendingValidation ? listeners : {})}
      >
        <Typography variant="h6">{node.word}</Typography>
        {node.pendingValidation && onCancel && (
          <CancelButton
            className="cancel-button"
            size="small"
            onClick={onCancel}
            aria-label="cancel validation"
          >
            <CloseIcon />
          </CancelButton>
        )}
        {!isFirst && !isLast && !node.pendingValidation && (
          <DeleteButton
            className="delete-button"
            size="small"
            onClick={onDelete}
            aria-label="delete word"
          >
            <Typography className="delete-x">×</Typography>
          </DeleteButton>
        )}
      </WordTile>
    </Box>
  );
}; 